/**
 * ScrollToBottomFAB — small clay round FAB shown when the user has
 * scrolled up in the chat stream. Tap = smooth-scroll to bottom.
 *
 * The FAB lives as an absolutely-positioned sibling of the stream.
 * It is appended/detached by ChatView, which also drives `setHidden()`
 * via a scroll listener on the stream.
 */

import { Icon } from "./Icon";

export interface ScrollFABOpts {
  stream: HTMLElement;
  onJump?: () => void;
}

/** Returns { el, sync, dispose }. `sync()` reads stream scroll + toggles visibility. */
export function ScrollToBottomFAB(opts: ScrollFABOpts): {
  el: HTMLButtonElement;
  sync: () => void;
  showIfAway: () => void;
  dispose: () => void;
} {
  const { stream, onJump } = opts;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "chat-view__scroll-fab";
  btn.setAttribute("aria-label", "Scroll to latest");
  btn.appendChild(Icon("arrowDown", 18));

  // Threshold: treat "near bottom" as within 80px of the bottom so a
  // single short pull doesn't immediately re-hide the FAB.
  const NEAR_PX = 80;

  function atBottom(): boolean {
    const remaining = stream.scrollHeight - stream.clientHeight - stream.scrollTop;
    return remaining <= NEAR_PX;
  }

  function sync(): void {
    btn.classList.toggle("chat-view__scroll-fab--visible", !atBottom());
  }

  function showIfAway(): void {
    // Called by ChatView on new msg.delta — only flash the FAB in if
    // the user is actually scrolled up. If at bottom we silently let
    // the auto-scroll keep up.
    if (!atBottom()) {
      btn.classList.add("chat-view__scroll-fab--visible");
    }
  }

  function onScroll(): void { sync(); }

  function onClick(): void {
    stream.scrollTo({ top: stream.scrollHeight, behavior: "smooth" });
    btn.classList.remove("chat-view__scroll-fab--visible");
    onJump?.();
  }

  stream.addEventListener("scroll", onScroll, { passive: true });
  btn.addEventListener("click", onClick);

  function dispose(): void {
    stream.removeEventListener("scroll", onScroll);
    btn.removeEventListener("click", onClick);
  }

  // Initial state — wait one frame so the stream has a height.
  requestAnimationFrame(sync);

  return { el: btn, sync, showIfAway, dispose };
}

export default ScrollToBottomFAB;
